import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter } from '@fortawesome/free-solid-svg-icons';
import './CohortOverview.css';

// Mock cohort data - 8 different omics
const mockCohorts = [
  {
    id: 1,
    title: 'Cancer Genomics Cohort',
    totalPopulation: 1250,
    omics: [
      { name: 'Genomics', count: 1250, total: 1250 },
      { name: 'Transcriptomics', count: 987, total: 1250 },
      { name: 'Proteomics', count: 845, total: 1250 },
      { name: 'Metabolomics', count: 723, total: 1250 },
      { name: 'Epigenomics', count: 654, total: 1250 },
      { name: 'Lipidomics', count: 543, total: 1250 },
      { name: 'Glycomics', count: 432, total: 1250 },
      { name: 'Microbiomics', count: 321, total: 1250 }
    ]
  },
  {
    id: 2,
    title: 'Cardiovascular Disease',
    totalPopulation: 890,
    omics: [
      { name: 'Genomics', count: 890, total: 890 },
      { name: 'Transcriptomics', count: 754, total: 890 },
      { name: 'Proteomics', count: 623, total: 890 },
      { name: 'Metabolomics', count: 567, total: 890 },
      { name: 'Epigenomics', count: 445, total: 890 },
      { name: 'Lipidomics', count: 378, total: 890 },
      { name: 'Glycomics', count: 289, total: 890 },
      { name: 'Microbiomics', count: 234, total: 890 }
    ]
  },
  {
    id: 3,
    title: 'Neurological Disorders',
    totalPopulation: 675,
    omics: [
      { name: 'Genomics', count: 675, total: 675 },
      { name: 'Transcriptomics', count: 598, total: 675 },
      { name: 'Proteomics', count: 456, total: 675 },
      { name: 'Metabolomics', count: 423, total: 675 },
      { name: 'Epigenomics', count: 367, total: 675 },
      { name: 'Lipidomics', count: 289, total: 675 },
      { name: 'Glycomics', count: 234, total: 675 },
      { name: 'Microbiomics', count: 187, total: 675 }
    ]
  },
  {
    id: 4,
    title: 'Metabolic Syndrome',
    totalPopulation: 1450,
    omics: [
      { name: 'Genomics', count: 1450, total: 1450 },
      { name: 'Transcriptomics', count: 1234, total: 1450 },
      { name: 'Proteomics', count: 1087, total: 1450 },
      { name: 'Metabolomics', count: 967, total: 1450 },
      { name: 'Epigenomics', count: 823, total: 1450 },
      { name: 'Lipidomics', count: 734, total: 1450 },
      { name: 'Glycomics', count: 612, total: 1450 },
      { name: 'Microbiomics', count: 478, total: 1450 }
    ]
  },
  {
    id: 5,
    title: 'Immunological Studies',
    totalPopulation: 523,
    omics: [
      { name: 'Genomics', count: 523, total: 523 },
      { name: 'Transcriptomics', count: 445, total: 523 },
      { name: 'Proteomics', count: 389, total: 523 },
      { name: 'Metabolomics', count: 312, total: 523 },
      { name: 'Epigenomics', count: 267, total: 523 },
      { name: 'Lipidomics', count: 234, total: 523 },
      { name: 'Glycomics', count: 198, total: 523 },
      { name: 'Microbiomics', count: 156, total: 523 }
    ]
  },
  {
    id: 6,
    title: 'Aging & Longevity',
    totalPopulation: 798,
    omics: [
      { name: 'Genomics', count: 798, total: 798 },
      { name: 'Transcriptomics', count: 687, total: 798 },
      { name: 'Proteomics', count: 578, total: 798 },
      { name: 'Metabolomics', count: 489, total: 798 },
      { name: 'Epigenomics', count: 423, total: 798 },
      { name: 'Lipidomics', count: 356, total: 798 },
      { name: 'Glycomics', count: 289, total: 798 },
      { name: 'Microbiomics', count: 234, total: 798 }
    ]
  }
];

function CohortOverview({ theme }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const getProgressWidth = (count, total) => {
    if (total === 0) return 0;
    return (count / total) * 100;
  };

  const filteredCohorts = useMemo(() => {
    if (!searchTerm) return mockCohorts;
    
    return mockCohorts.filter(cohort => 
      cohort.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cohort.omics.some(omic => omic.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm]);

  return (
    <div className="cohort-overview">
      {/* Title */}
      <h1 className="cohort-overview__title">
        Cohort Overview
      </h1>
      
      {/* Search Bar */}
      <div className="cohort-overview__search-bar">
        <div className="cohort-overview__search">
          <FontAwesomeIcon icon={faSearch} className="cohort-overview__search-icon" />
          <input
            type="text"
            placeholder="Search cohorts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cohort-overview__search-input"
          />
        </div>
        <button 
          className={`cohort-overview__filter-toggle ${showFilters ? 'cohort-overview__filter-toggle--active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
        </button>
      </div>

      {filteredCohorts.length === 0 ? (
        <div className="cohort-overview__no-results">
          <FontAwesomeIcon icon={faSearch} />
          <h3>No cohorts found</h3>
          <p>Try adjusting your search term</p>
        </div>
      ) : (
        <div className="cohort-overview__content">
          <div className="cohort-grid">
            {filteredCohorts.map((cohort) => (
              <div key={cohort.id} className="cohort-tile">
                <div className="cohort-tile__header">
                  <span className="cohort-tile__title">{cohort.title}</span>
                  <span className="cohort-tile__id">#{cohort.id}</span>
                </div>
                <div className="cohort-tile__content">
                  {cohort.omics.map((omic, index) => (
                    <div key={index} className="cohort-tile__omic-item">
                      <div className="cohort-tile__omic-header">
                        <span className="cohort-tile__omic-name">{omic.name}</span>
                        <span className="cohort-tile__omic-count">{omic.count}/{omic.total}</span>
                      </div>
                      <div className="cohort-tile__progress-bar">
                        <div 
                          className="cohort-tile__progress-fill" 
                          style={{ width: `${getProgressWidth(omic.count, omic.total)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cohort-tile__footer">
                  <span className="cohort-tile__total">Total Population: {cohort.totalPopulation}</span>
                  <span className="cohort-tile__date">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CohortOverview;